import { Bot, User, Download, FileText, Zap } from 'lucide-react';
import type { Message } from '../types';
import clsx from 'clsx';
import { useMemo } from 'react';

interface ChatMessageProps {
  message: Message;
}

/**
 * Render markdown text as React elements.
 * Handles: headings, **bold**, *italic*, `code`, code blocks, bullet lists, numbered lists, line breaks.
 */
function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      elements.push(
        <pre key={`code-${i}`} className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 my-2 overflow-x-auto text-xs font-mono text-zinc-200 whitespace-pre-wrap">
          {lang && <span className="text-zinc-500 text-[10px] block mb-1">{lang}</span>}
          {codeLines.join('\n')}
        </pre>
      );
      continue;
    }

    // Heading 3
    if (line.startsWith('### ')) {
      elements.push(
        <h3 key={`h3-${i}`} className="text-sm font-semibold text-white mt-3 mb-1">
          {inlineMarkdown(line.slice(4))}
        </h3>
      );
      i++;
      continue;
    }

    // Heading 2
    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={`h2-${i}`} className="text-base font-semibold text-white mt-3 mb-1">
          {inlineMarkdown(line.slice(3))}
        </h2>
      );
      i++;
      continue;
    }

    // Heading 1
    if (line.startsWith('# ')) {
      elements.push(
        <h1 key={`h1-${i}`} className="text-lg font-bold text-white mt-3 mb-1">
          {inlineMarkdown(line.slice(2))}
        </h1>
      );
      i++;
      continue;
    }

    // Unordered list
    if (line.match(/^[-*+] /)) {
      const items: string[] = [];
      while (i < lines.length && lines[i].match(/^[-*+] /)) {
        items.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} className="list-disc list-inside space-y-0.5 my-1 text-zinc-200">
          {items.map((item, idx) => (
            <li key={idx} className="text-sm">{inlineMarkdown(item)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (line.match(/^\d+\. /)) {
      const items: string[] = [];
      while (i < lines.length && lines[i].match(/^\d+\. /)) {
        items.push(lines[i].replace(/^\d+\. /, ''));
        i++;
      }
      elements.push(
        <ol key={`ol-${i}`} className="list-decimal list-inside space-y-0.5 my-1 text-zinc-200">
          {items.map((item, idx) => (
            <li key={idx} className="text-sm">{inlineMarkdown(item)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Horizontal rule
    if (line.match(/^---+$/)) {
      elements.push(<hr key={`hr-${i}`} className="border-zinc-700 my-2" />);
      i++;
      continue;
    }

    // Empty line → spacer
    if (line.trim() === '') {
      elements.push(<div key={`sp-${i}`} className="h-1" />);
      i++;
      continue;
    }

    // Regular paragraph
    elements.push(
      <p key={`p-${i}`} className="text-sm text-zinc-100 leading-relaxed">
        {inlineMarkdown(line)}
      </p>
    );
    i++;
  }

  return <>{elements}</>;
}

/**
 * Apply inline markdown: **bold**, *italic*, `code`
 */
function inlineMarkdown(text: string): React.ReactNode {
  // Split on bold, italic, and inline code patterns
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={idx} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={idx} className="italic">{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={idx} className="bg-zinc-700 px-1.5 py-0.5 rounded text-xs font-mono text-progeny-300">
          {part.slice(1, -1)}
        </code>
      );
    }
    return part;
  });
}

export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === 'user';
  const isStreaming = message.streaming === true;

  // Check if message contains SVG pattern data
  const hasSvgPattern = useMemo(() => {
    return message.content.includes('<svg') && message.content.includes('viewBox');
  }, [message.content]);

  return (
    <div className={clsx('flex gap-3 message-enter', isUser ? 'flex-row-reverse' : '')}>
      {/* Avatar */}
      <div
        className={clsx(
          'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5',
          isUser ? 'bg-zinc-700' : 'bg-progeny-600'
        )}
      >
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>

      <div className={clsx('flex-1 max-w-[80%]', isUser ? 'text-right' : '')}>
        {/* Main content bubble */}
        <div
          className={clsx(
            'inline-block px-4 py-3 rounded-2xl text-sm w-full',
            isUser
              ? 'bg-progeny-600 text-white rounded-br-md'
              : 'bg-zinc-800 text-zinc-100 rounded-bl-md'
          )}
        >
          {hasSvgPattern ? (
            // SVG pattern display
            <div
              className="pattern-container bg-zinc-900 rounded p-3 overflow-auto max-h-96"
              dangerouslySetInnerHTML={{ __html: message.content }}
            />
          ) : isUser ? (
            // User messages: plain text
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          ) : (
            // Assistant messages: full markdown rendering
            <div className="space-y-0.5">
              {message.content ? (
                renderMarkdown(message.content)
              ) : isStreaming ? null : (
                <p className="text-zinc-500 text-sm italic">No response</p>
              )}

              {/* Streaming cursor */}
              {isStreaming && (
                <span className="inline-block w-2 h-4 bg-progeny-400 rounded-sm animate-pulse ml-0.5 align-middle" />
              )}
            </div>
          )}
        </div>

        {/* Attachments */}
        {message.attachments && message.attachments.length > 0 && (
          <div className="mt-2 space-y-2">
            {message.attachments.map((att) => (
              <div
                key={att.id}
                className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-800 rounded-lg text-sm"
              >
                {att.type === 'image' ? (
                  <img
                    src={att.url}
                    alt="Uploaded"
                    className="w-20 h-20 object-cover rounded"
                  />
                ) : att.type === 'dxf' ? (
                  <a
                    href={att.url}
                    download={att.name || 'pattern.dxf'}
                    className="flex items-center gap-2 text-progeny-400 hover:text-progeny-300"
                  >
                    <Download className="w-4 h-4" />
                    Download DXF
                  </a>
                ) : (
                  <span className="flex items-center gap-2 text-zinc-300">
                    <FileText className="w-4 h-4" />
                    {att.name}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pattern generation indicator (shown after streaming completes) */}
        {message.patternGenerated && !isStreaming && (
          <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/20 rounded-lg text-xs text-green-400">
            <Zap className="w-3 h-3" />
            Pattern spec generated
            {message.garmentSpec?.garmentType && ` — ${message.garmentSpec.garmentType}`}
          </div>
        )}
      </div>
    </div>
  );
}
